import React, { useState, useContext, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../../index";
import { toast } from "react-toastify";
import ConfirmToast from "../../forms/notification/Confirm";
import ACControlList from "./components/ACControlList";
import FirmwareUpdateBlock from "./components/FirmwareUpdateBlock";
import FirmwareSelector from "./components/FirmwareSelector";
import LogsViewer from "./components/LogsViewer";
import FirmwareOutcomeNotice from "./components/FirmwareOutcomeNotice";
import { FirmwareType, FirmwareFiles, FirmwareIds } from "./types";
import { useFirmwareUpdate } from "./useFirmwareUpdate";
import {
  imageProductForDevice,
  isFirmwareSuitable,
  productTitle,
  productsForType,
} from "./firmwareProducts";
import "./style.css";

const FirmwareUpdatePanel: React.FC = () => {
  const { adminStore, uploadStore } = useContext(Context);
  const module = adminStore.adminModule;
  const AC = adminStore.AC;

  const [files, setFiles] = useState<FirmwareFiles>({});
  const [uploading, setUploading] = useState<FirmwareType | null>(null);
  const [firmIds, setFirmIds] = useState<FirmwareIds>({
    ESP: null,
    STM: null,
    AC_control: null,
  });
  // `ОБН.3`: выбранное изделие для загрузки — по каждому типу отдельно, без
  // значения по умолчанию: человек обязан выбрать, для какого изделия образ.
  const [products, setProducts] = useState<Record<FirmwareType, string>>({
    ESP: "",
    STM: "",
    AC_control: "",
  });
  const [error, setError] = useState<string | null>(null);
  // `ОБН.2`: запуск, опрос и его предел — в `useFirmwareUpdate`; здесь только
  // показ отказа и разметка.
  const {
    updateLogs,
    outcomes,
    startUpdate: runUpdate,
    resumePolling,
    stopPolling: stopUpdatePolling,
  } = useFirmwareUpdate(adminStore);

  // `ОБН.4` (`aq-130` (б)): у ММБ станций нет — блока `AC_control` и списка
  // станций не показываем. Неизвестный тип — три блока и пометка в заголовке.
  const deviceType = String(module?.device_type || "").toLowerCase();
  const firmwareTypes: readonly FirmwareType[] =
    deviceType === "mmb" ? ["ESP", "STM"] : ["ESP", "STM", "AC_control"];
  const updatingType = adminStore.updatingType as FirmwareType | null;

  useEffect(() => {
    adminStore.fetchFirmWare();
  }, []);

  useEffect(() => {
    // `ОБН.2`: вкладку переоткрыли во время обновления — опрос продолжается,
    // но уже с пределом 15 мин от сохранённого запуска.
    resumePolling();
  }, []);

  const handleUpload = async (type: FirmwareType) => {
    const file = files[type];
    if (!file) {
      setError("Сначала выберите файл");
      return;
    }
    // `ОБН.3`: без изделия загрузка не начинается — сервер ответил бы 400.
    const product = products[type];

    if (!product) {
      setError(`Выберите изделие для образа ${type}`);
      return;
    }

    // Устанавливаем состояние загрузки для конкретного типа
    setUploading(type);

    // Вызываем метод из uploadStore
    const result = await uploadStore.uploadFirmware(file, type, false, products[type]);

    // Обрабатываем результат
    if (result?.conflict) {
      // Если файл существует, показываем диалог подтверждения
      toast(
        <ConfirmToast
          message={`Файл "${result.filename}" уже существует. Заменить его?`}
          onConfirm={() => replaceFirmware(type, file)} // Вызываем replaceFirmware с файлом
          onCancel={() => {
            toast.info("Загрузка отменена");
            // Сбрасываем состояние загрузки, если пользователь отменил
            setUploading(null);
          }}
          type={"del"}
          title={"заменить"}
        />,
        {
          autoClose: false,
          closeOnClick: false,
        },
      );
      // ВАЖНО: Не сбрасываем setUploading(null) здесь, потому что процесс может продолжиться
      // через replaceFirmware или onCancel
      return; // Выходим, чтобы не выполнить код ниже
    }

    if (result?.success) {
      // Если успешно загружено
      setFiles((prev) => ({ ...prev, [type]: undefined })); // Очищаем выбранный файл
      adminStore.fetchFirmWare(); // Обновляем список прошивок в сторе
      toast.success(`Прошивка ${type} успешно загружена`);
    } else {
      // Если была ошибка
      setError(
        `Не удалось загрузить ${type}: ${result?.message || "Неизвестная ошибка"}`,
      );
    }

    // В любом случае (успех, ошибка, но НЕ конфликт), сбрасываем состояние загрузки
    setUploading(null);
  };

  // --- ЗАМЕНИТЬ ЭТУ ФУНКЦИЮ ---
  const replaceFirmware = async (
    type: FirmwareType,
    file: File,
  ) => {
    // Вызываем метод из uploadStore с флагом replace = true
    const result = await uploadStore.uploadFirmware(
      file,
      type,
      true /* replace */,
      products[type],
    );

    if (result?.success) {
      // Если успешно заменено
      setFiles((prev) => ({ ...prev, [type]: undefined })); // Очищаем выбранный файл
      adminStore.fetchFirmWare(); // Обновляем список прошивок в сторе
      toast.success(`Прошивка ${type} успешно заменена`);
    } else {
      // Если была ошибка при замене
      toast.error(result?.message || "Не удалось заменить прошивку");
    }

    // Обязательно сбрасываем состояние загрузки после завершения
    setUploading(null);
  };

  // ... остальной код компонента (startUpdate, startPolling, stopPolling, return ...)

  const startUpdate = async (
    firmId: number,
    moduleId: string,
    type: FirmwareType,
  ) => {
    if (!firmId || firmId <= 0) {
      alert("Выберите прошивку");
      return;
    }

    setError(null);

    // `ОБН.2` (`АРХ-Н22`): отказ запуска виден текстом ответа сервера, и
    // опрос в этом случае не начинается — `startUpdate` вернёт текст отказа.
    const message = await runUpdate(firmId, moduleId, type);

    if (message) {
      setError(message);
    }
  };

  const stopPolling = () => {
    stopUpdatePolling();
    toast.info("Отслеживание обновлений остановлено");
  };

  return (
    <div className="FW-panel">
      <div className="FW-management">
        <FirmwareSelector onStopPolling={stopPolling} />

        {module && (
          <div>
            {firmwareTypes.map((type) => {
              const currentVersion = module[type] || "";
              // `ОБН.3`: в блоке — только образы, подходящие изделию модуля.
              const availableFirmwares = adminStore.firmWares.filter(
                (fw) =>
                  fw.type === type &&
                  isFirmwareSuitable(fw, type, deviceType),
              );

              return (
                <FirmwareUpdateBlock
                  key={type}
                  type={type}
                  currentVersion={currentVersion}
                  firmwares={availableFirmwares}
                  selectedId={firmIds[type]}
                  onIdChange={(id) =>
                    setFirmIds((prev) => ({ ...prev, [type]: id }))
                  }
                  file={files[type]}
                  uploading={uploading === type}
                  onFileChange={(file) => {
                    setFiles((prev) => ({ ...prev, [type]: file }));
                    // Сбрасываем uploading для этого типа
                    if (uploading === type) {
                      setUploading(null);
                    }
                  }}
                  products={productsForType(type)}
                  product={products[type]}
                  onProductChange={(product) =>
                    setProducts((prev) => ({ ...prev, [type]: product }))
                  }
                  onUpload={() => handleUpload(type)}
                  onStartUpdate={() =>
                    startUpdate(firmIds[type]!, module.charge_id, type)
                  }
                  disabledUpdate={!firmIds[type]}
                  isUpdating={updatingType === type}
                  updateStatus={
                    adminStore.updatingType === type
                      ? undefined
                      : adminStore.updateStatus[type]
                  }
                />
              );
            })}
            {deviceType !== "mmb" ? (
              <ACControlList items={Array.isArray(AC) ? AC : []} />
            ) : null}

            {error && (
              <div
                className="FW-error"
                style={{ color: "red", marginTop: "10px" }}
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      <LogsViewer logs={updateLogs} />

      {/* DEV-60: каждый отказ — с номером станции и причиной; итог успеха —
          одной строкой. Отказ станции, отказ STM и отказ ESP показываются
          одинаково. */}
      {outcomes
        .filter((outcome) => outcome.outcome === "failure" || outcome.final)
        .map((outcome, index) => (
          <FirmwareOutcomeNotice
            key={`${index}-${outcome.component}-${outcome.reason}-${outcome.stationNumber}`}
            outcome={outcome}
          />
        ))}

      {/* 2. Список прошивок (по центру) */}
      <div className="FW-firmwares">
        <h3>Доступные прошивки</h3>
        <div
          className="FW-firmwares-list"
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            fontSize: "13px",
          }}
        >
          {adminStore.firmWares.length === 0 ? (
            <p style={{ color: "#666", fontStyle: "italic" }}>
              Нет доступных прошивок
            </p>
          ) : (
            adminStore.firmWares.map((fw) => (
              <div
                key={fw.id}
                className="FW-firmwareItem"
                style={{
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  marginBottom: "4px",
                  lineHeight: "1.4",
                }}
              >
                <div>
                  <strong>{fw.type}</strong>: {fw.version}
                  {/* `ОБН.3`: образ без изделия не предлагается к запуску, но
                      виден — чтобы root знал, что его надо загрузить заново. */}
                  <span className="FW-firmware-product">
                    {" "}
                    — {fw.product ? productTitle(fw.product) : "не привязан"}
                  </span>
                  <br />
                </div>
                <button
                  onClick={() => adminStore.deleteFirmWare(fw.id)}
                  style={{
                    width: "30px",
                    height: "30px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    lineHeight: "1",
                    borderRadius: "4px",
                    backgroundColor: "#ff5454",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  X
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(FirmwareUpdatePanel);
